/**
 * @clearpath/auth-client
 *
 * OIDC client targeting auth.molaris.ai. Apps construct a client once at init:
 *
 * ```ts
 * import { createAuthClient, AuthProvider } from "@clearpath/auth-client";
 * const client = createAuthClient(); // reads VITE_* env vars
 * <AuthProvider client={client}>...</AuthProvider>
 * ```
 *
 * Apollo links call `client.getAccessToken()` / `client.onAuthError()` via the
 * captured handle. For code paths that can't easily capture the handle (e.g.
 * the `@clearpath/data-client` package, legacy admin wiring), a lazily-built
 * default singleton is exposed through back-compat free functions
 * (`getAccessToken`, `getCurrentUser`, `signIn`, `signOut`, `onAuthError`,
 * `registerAuthErrorListener`). They delegate to `createAuthClient()` using the
 * standard VITE_* env vars. New code should prefer the explicit client handle.
 *
 * Security invariants (see docs/clearpath-architecture/11-sessions-and-tokens.md):
 *   - access + ID tokens live in memory only
 *   - refresh cookie is the host-only `__Host-molaris-refresh` (no Domain) on
 *     the auth host — httpOnly / Secure / SameSite=None (11-sessions-and-tokens.md:210;
 *     `None`, NOT `Lax`, is why the origin/CSRF checks exist, and it is host-only,
 *     NOT `.molaris.ai`). Those checks are the PRESENCE predicate in
 *     cookies/refresh.ts (`hasRefreshCookie`) enforced by the auth handlers —
 *     handler.ts's /logout and /me, oidc/http_routes.ts's /refresh and
 *     oidc/token.ts — not by cookies/refresh.ts itself. The legacy
 *     `molaris_refresh` cookie is still a live read fallback for the ENG-2127
 *     migration (parseRefreshCookie reads it by NAME, so it covers the host-only
 *     legacy variant as well as the parent-domain ones), not deletion-only.
 *   - no tokens in localStorage / sessionStorage (oidc-client-ts only stores
 *     transient PKCE state in sessionStorage during the redirect dance)
 */

// -- Public types ------------------------------------------------------------
export type {
  AuthAudience,
  AuthError,
  AuthErrorListener,
  MfaChallenge,
  MfaFactor,
  MfaState,
  PortalRole,
  Session,
  SessionListener,
  Tenant,
} from "./types.js";

// -- Config ------------------------------------------------------------------
export { loadAuthConfig, type AuthConfig } from "./config.js";

// -- Core client -------------------------------------------------------------
export {
  createAuthClient,
  type AuthClient,
  type CreateAuthClientOptions,
  type SignInOptions,
  type SignOutOptions,
} from "./client.js";

// -- Sign-out result contract (ENG-2907) -------------------------------------
export type {
  SignOutResult,
  SignOutFailureReason,
  SignOutProbeObservation,
} from "./sign-out.js";

// -- MFA ---------------------------------------------------------------------
export {
  AuthServiceError,
  createMfaHelpers,
  MfaNotEnrolledError,
  StepUpFactorUnsupportedError,
  type MfaHelpers,
  type StepUpRequest,
} from "./mfa.js";

// -- Account security (password + MFA + recovery-code self-service) ---------
export {
  AccountSecurityError,
  createAccountSecurityHelpers,
  type AccountSecurityHelpers,
  type ChangePasswordInput,
  type StepUpRetryOptions,
} from "./account-security.js";

// -- Password reset (self-service forgot-password) ---------------------------
export {
  createPasswordResetHelpers,
  type PasswordResetHelpers,
  type ConfirmPasswordResetInput,
} from "./password-reset.js";

// -- Hosted password reset (ENG-3766: SPA reset routes redirect to auth) ----
export {
  buildHostedPasswordResetUrl,
  redirectToHostedPasswordReset,
  type HostedPasswordResetPage,
  type ResetLocation,
} from "./hosted-password-reset.js";

// -- WebAuthn primitives (shared by step-up + enrollment) -------------------
export {
  base64UrlToBuffer,
  bufferToBase64Url,
  createCredential,
  decodeAuthenticationOptions,
  decodeRegistrationOptions,
  getAssertion,
  isWebAuthnRegistrationSupported,
  isWebAuthnSupported,
  serializeAssertion,
  serializeRegistration,
  WebAuthnClientError,
  type EncodedAuthenticationOptions,
  type EncodedRegistrationOptions,
  type SerializedAssertion,
  type SerializedRegistration,
  type WebAuthnClientErrorCode,
} from "./webauthn.js";

// -- Silent refresh (exposed for advanced use / testing) --------------------
export {
  createSilentRefreshScheduler,
  TransientRefreshError,
  type SilentRefreshScheduler,
} from "./silent-refresh.js";

// -- React surface (re-exported from root for convenience) ------------------
export {
  AuthProvider,
  useAuth,
  useRequireAuth,
  TenantPicker,
  HostedPasswordResetRedirect,
  type AuthContextValue,
  type AuthProviderProps,
  type HostedPasswordResetRedirectProps,
  type TenantPickerProps,
} from "./react/index.js";

export {
  useIdleTimeout,
  useIdleTimeoutContext,
  IdleTimeoutProvider,
  resolveIdleMinutes,
  type IdleTimeoutContextValue,
  type IdleLogoutFailure,
  type IdleTimeoutProviderProps,
  type ResolveIdleMinutesInput,
  type UseIdleTimeoutOptions,
  type UseIdleTimeoutResult,
} from "./react/useIdleTimeout.js";

// ---------------------------------------------------------------------------
// Back-compat free-function surface.
//
// Kept so @clearpath/data-client and the apps/admin wiring can continue to
// import module-level helpers without threading an AuthClient handle. All of
// these delegate to a lazily-built default AuthClient that uses the same
// VITE_* env vars apps/main, apps/portal, apps/account etc. already set.
// ---------------------------------------------------------------------------
import {
  createAuthClient,
  type AuthClient,
  type SignInOptions,
  type SignOutOptions,
} from "./client.js";
import type { SignOutResult } from "./sign-out.js";
import { createMfaHelpers, type MfaHelpers, type StepUpRequest } from "./mfa.js";
import {
  createPasswordResetHelpers,
  type PasswordResetHelpers,
  type ConfirmPasswordResetInput,
} from "./password-reset.js";
import type { AuthError, AuthErrorListener, Session } from "./types.js";

let _defaultClient: AuthClient | null = null;

/**
 * Get the process-wide default AuthClient. Built lazily so apps that pass an
 * explicit client to `<AuthProvider>` never construct a second one (and so
 * that test suites importing the package without a browser env don't trip
 * oidc-client-ts during module load).
 */
function getDefaultClient(): AuthClient {
  if (!_defaultClient) {
    _defaultClient = createAuthClient();
  }
  return _defaultClient;
}

export function registerAuthErrorListener(listener: AuthErrorListener): () => void {
  return getDefaultClient().onAuthError(listener);
}

export function onAuthError(error?: AuthError): void {
  getDefaultClient().emitAuthError(error);
}

export async function getAccessToken(): Promise<string | null> {
  return getDefaultClient().getAccessToken();
}

export async function getCurrentUser(): Promise<Session | null> {
  return getDefaultClient().getCurrentUser();
}

/**
 * Kick off the OIDC PKCE redirect. The legacy `(email, password)` signature
 * is dropped; auth.molaris.ai owns the credential exchange. Callers that
 * need tenant-scoped sign-in should use the client handle directly.
 *
 * ENG-101 — accepts `SignInOptions` (notably `prompt: 'login'` +
 * `loginHint`) so callers using this free-function surface (e.g. apps that
 * haven't migrated to an explicit client handle) can still force the hosted
 * sign-in form on an explicit "Sign in" action instead of risking a silent
 * resolve to a stale cookie's identity.
 */
export async function signIn(options?: SignInOptions): Promise<void> {
  return getDefaultClient().signIn(options);
}

export async function signOut(options?: SignOutOptions): Promise<SignOutResult> {
  return getDefaultClient().signOut(options);
}

/** Complete the PKCE callback at /auth/callback (exchange code → tokens). */
export async function handleCallback(): Promise<Session> {
  return getDefaultClient().handleCallback();
}

/** Test-only: tear down the default singleton. */
export function __resetDefaultAuthClientForTests(): void {
  _defaultClient = null;
  _defaultMfaHelpers = null;
  _defaultPasswordResetHelpers = null;
}

// ---------------------------------------------------------------------------
// Password-reset convenience surface.
//
// Free-function API backed by the same default AuthClient so a pre-auth page
// can trigger a reset without threading a client handle. (The apps/main and
// apps/portal reset pages that used it now redirect to the auth-hosted reset
// pages instead — ENG-3766 — but the JSON wrappers stay public.) The endpoints are unauthenticated + pool-
// agnostic, so no audience/token is involved beyond the base URL.
// ---------------------------------------------------------------------------

let _defaultPasswordResetHelpers: PasswordResetHelpers | null = null;

function getDefaultPasswordResetHelpers(): PasswordResetHelpers {
  if (!_defaultPasswordResetHelpers) {
    _defaultPasswordResetHelpers = createPasswordResetHelpers(getDefaultClient());
  }
  return _defaultPasswordResetHelpers;
}

/** Kick off a self-service password reset. Resolves on the generic success
 *  envelope regardless of whether the email exists (anti-enumeration). */
export async function requestPasswordReset(email: string): Promise<void> {
  return getDefaultPasswordResetHelpers().requestPasswordReset(email);
}

/** Complete a self-service reset with the emailed code + a new password. */
export async function confirmPasswordReset(
  input: ConfirmPasswordResetInput,
): Promise<void> {
  return getDefaultPasswordResetHelpers().confirmPasswordReset(input);
}

// ---------------------------------------------------------------------------
// MFA convenience surface.
//
// Thin free-function API backed by the same default AuthClient. Used by
// @clearpath/auth-ui's StepUpPromptModal and by apps/admin's JIT approval
// flow so those code paths don't need to thread an AuthClient handle.
// ---------------------------------------------------------------------------

let _defaultMfaHelpers: MfaHelpers | null = null;

function getDefaultMfaHelpers(): MfaHelpers {
  if (!_defaultMfaHelpers) {
    _defaultMfaHelpers = createMfaHelpers(getDefaultClient());
  }
  return _defaultMfaHelpers;
}

/** Request a step-up challenge for the named sensitive action. Resolves with
 *  `{ required: false }` when the caller's verification is already fresh, and
 *  rejects with `MfaNotEnrolledError` when no factor can satisfy it. */
export async function requestStepUp(
  action: string,
  maxAgeMinutes?: number,
): Promise<StepUpRequest> {
  return getDefaultMfaHelpers().requestStepUp(action, maxAgeMinutes);
}

/** Submit a TOTP response to a previously-issued step-up challenge. */
export async function respondToTotp(
  challengeId: string,
  code: string,
): Promise<Session> {
  return getDefaultMfaHelpers().respondToTotp(challengeId, code);
}

/** Submit a WebAuthn assertion to a previously-issued step-up challenge. */
export async function respondToWebAuthn(
  challengeId: string,
  assertion: PublicKeyCredential,
): Promise<Session> {
  return getDefaultMfaHelpers().respondToWebAuthn(challengeId, assertion);
}

/**
 * End-to-end WebAuthn step-up: request challenge, prompt user, submit
 * assertion. Resolves with the refreshed session on success; rejects with
 * `WebAuthnClientError` on user-cancel / timeout / unsupported browser.
 */
export async function performWebAuthnStepUp(
  action: string,
  maxAgeMinutes?: number,
): Promise<Session> {
  return getDefaultMfaHelpers().performWebAuthnStepUp(action, maxAgeMinutes);
}
